import { yupResolver } from '@hookform/resolvers/yup';
import { Menu01Icon } from '@hugeicons/core-free-icons';
import {
  useCreateGramWithStorage,
  useUpdateSession,
} from '@sunrise-ui/api-client';
import { SessionStatus } from '@sunrise-ui/api/events/api';
import {
  AuthFlowError,
  Button,
  Colors,
  DateSelector,
  IconButton,
  InputError,
  Modal,
  TextReg,
  Tooltip,
  menuContentStyle,
} from '@sunrise-ui/primitives';
import React, { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import type { GestureResponderEvent, ViewStyle } from 'react-native';
import { StyleSheet, View } from 'react-native';
import { Menu } from 'react-native-paper';
import * as yup from 'yup';

export type Props = {
  sessionId: string;
  eventId: string;
  currentStatus?: SessionStatus;
  speakerUserIds: string[];
  communityId?: string;
  existingStartDate?: string | null;
  existingEndDate?: string | null;
  style?: ViewStyle;
};

const STATUS_LABELS: Record<SessionStatus, string> = {
  [SessionStatus.AwaitingReview]: 'Awaiting Review',
  [SessionStatus.Accepted]: 'Accepted',
  [SessionStatus.Voting]: 'Voting',
  [SessionStatus.Scheduled]: 'Scheduled',
  [SessionStatus.Confirmed]: 'Confirmed',
  [SessionStatus.Rejected]: 'Rejected',
};

const ALL_STATUSES: SessionStatus[] = [
  SessionStatus.AwaitingReview,
  SessionStatus.Accepted,
  SessionStatus.Voting,
  SessionStatus.Scheduled,
  SessionStatus.Confirmed,
  SessionStatus.Rejected,
];

export function SessionStatusMenuButton({
  sessionId,
  eventId,
  currentStatus,
  speakerUserIds,
  communityId,
  existingStartDate,
  existingEndDate,
  style,
}: Props) {
  const [isMenuVisible, setIsMenuVisible] = useState(false);
  const [isScheduleModalVisible, setIsScheduleModalVisible] = useState(false);

  const { mutateAsync: updateSession, isPending: isUpdating } =
    useUpdateSession();
  const { mutateAsync: createGramWithStorage, isPending: isCreatingGram } =
    useCreateGramWithStorage();

  const isBusy = isUpdating || isCreatingGram;

  const handleOpenMenu = (e?: GestureResponderEvent) => {
    if (e?.stopPropagation) e.stopPropagation();
    setIsMenuVisible(true);
  };

  const handleSelectStatus = async (nextStatus: SessionStatus) => {
    // Close the menu immediately for responsiveness
    setIsMenuVisible(false);

    if (nextStatus === currentStatus) return;

    try {
      // If scheduling, open modal to collect start/end times before updating
      if (nextStatus === SessionStatus.Scheduled) {
        const hasExistingTimes = Boolean(existingStartDate && existingEndDate);
        if (hasExistingTimes) {
          await updateSession({
            sessionId,
            updateData: { status: SessionStatus.Scheduled },
            communityId,
            gramRegenerationUserIds: speakerUserIds,
          });
          return;
        }
        setIsScheduleModalVisible(true);
        return;
      }

      // Update the backend session status for other statuses immediately
      await updateSession({
        sessionId,
        updateData: { status: nextStatus },
        communityId,
      });

      if (nextStatus === SessionStatus.Voting && speakerUserIds.length > 0) {
        // Generate grams for each speaker
        const gramPromises = speakerUserIds.map((userId) =>
          createGramWithStorage({
            user_id: userId,
            event_id: eventId,
            google_drive_folder_id: '1a_EbBqivsMyVkESkOLsqqxlgAedxrtEg',
            file_format: 'PNG',
          }),
        );
        await Promise.allSettled(gramPromises);
      }
    } catch (error) {
      if (error instanceof AuthFlowError) return;
      console.error('Error applying session status change:', error);
    }
  };

  // Form types and schema for scheduling
  type ScheduleForm = {
    startDate: Date | null;
    endDate: Date | null;
  };

  const scheduleSchema: yup.ObjectSchema<ScheduleForm> = yup
    .object({
      startDate: yup
        .date()
        .typeError('Start time is required')
        .required('Start time is required')
        .nullable(),
      endDate: yup
        .date()
        .typeError('End time is required')
        .required('End time is required')
        .nullable()
        .when('startDate', {
          is: (val: unknown) => Boolean(val),
          then: (schema) =>
            schema.min(
              yup.ref('startDate'),
              'End time must be after start time',
            ),
        }),
    })
    .required();

  const {
    control,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset,
    watch,
    setValue,
    getValues,
  } = useForm<ScheduleForm>({
    resolver: yupResolver(scheduleSchema),
    defaultValues: { startDate: null, endDate: null },
    mode: 'onSubmit',
  });

  const { startDate } = watch();

  const handleCloseScheduleModal = () => {
    reset();
    setIsScheduleModalVisible(false);
  };

  const onSubmitSchedule = async ({ startDate, endDate }: ScheduleForm) => {
    if (!startDate || !endDate) return;

    try {
      await updateSession({
        sessionId,
        communityId,
        updateData: {
          status: SessionStatus.Scheduled,
          start_date: new Date(startDate).toISOString(),
          end_date: new Date(endDate).toISOString(),
        },
        gramRegenerationUserIds: speakerUserIds,
      });
      handleCloseScheduleModal();
    } catch (error) {
      console.error('Error scheduling session:', error);
    }
  };

  return (
    <>
      <Menu
        visible={isMenuVisible}
        onDismiss={() => setIsMenuVisible(false)}
        contentStyle={menuContentStyle}
        anchor={
          <Tooltip title="Change status">
            <IconButton
              icon={Menu01Icon}
              onPress={handleOpenMenu}
              size="small"
              style={style}
              disabled={isBusy}
            />
          </Tooltip>
        }
      >
        {ALL_STATUSES.map((status) => (
          <Menu.Item
            key={status}
            onPress={(e) => {
              if (e?.stopPropagation) e.stopPropagation();
              void handleSelectStatus(status);
            }}
            title={
              status === currentStatus
                ? `${STATUS_LABELS[status]} (current)`
                : STATUS_LABELS[status]
            }
            titleStyle={{ color: Colors.text }}
            disabled={isBusy || status === currentStatus}
          />
        ))}
      </Menu>

      <Modal
        visible={isScheduleModalVisible}
        hide={handleCloseScheduleModal}
        modalStyle={styles.modal}
        contentStyle={styles.modalContent}
      >
        <View style={styles.header}>
          <TextReg style={styles.title}>Schedule Session</TextReg>
          <TextReg style={styles.subtitle}>
            Please select the start and end times for this session.
          </TextReg>
        </View>

        <View style={styles.formRow}>
          <Controller
            control={control}
            name="startDate"
            render={({ field: { onChange, value } }) => (
              <DateSelector
                label="Start time"
                mode="datetime"
                value={value ?? undefined}
                onChange={(date) => {
                  const prevStart = getValues('startDate');
                  onChange(date);
                  const currentEnd = getValues('endDate');
                  const FORTY_FIVE_MIN_MS = 45 * 60 * 1000;
                  const endWasAuto =
                    !!prevStart &&
                    !!currentEnd &&
                    Math.abs(
                      currentEnd.getTime() -
                        (new Date(prevStart).getTime() + FORTY_FIVE_MIN_MS),
                    ) < 1000;
                  if (!currentEnd || endWasAuto) {
                    const autoEnd = new Date(date);
                    autoEnd.setMinutes(autoEnd.getMinutes() + 45);
                    setValue('endDate', autoEnd, {
                      shouldValidate: true,
                      shouldDirty: true,
                    });
                  }
                }}
                minimumDate={undefined}
                testID="schedule-start-date"
              />
            )}
          />
          <InputError fieldError={errors.startDate} />
        </View>

        <View style={styles.formRow}>
          <Controller
            control={control}
            name="endDate"
            render={({ field: { onChange, value } }) => (
              <DateSelector
                label="End time"
                mode="datetime"
                value={value ?? undefined}
                onChange={onChange}
                minimumDate={startDate ?? undefined}
                testID="schedule-end-date"
              />
            )}
          />
          <InputError fieldError={errors.endDate} />
        </View>

        <View style={styles.actions}>
          <Button
            mode="outlined"
            onPress={handleCloseScheduleModal}
            disabled={isSubmitting || isBusy}
          >
            Cancel
          </Button>
          <Button
            onPress={handleSubmit(onSubmitSchedule)}
            disabled={isSubmitting || isBusy}
          >
            Save
          </Button>
        </View>
      </Modal>
    </>
  );
}

const styles = StyleSheet.create({
  modal: {
    maxWidth: 520,
    width: '90%',
    alignSelf: 'center',
  },
  modalContent: {
    padding: 24,
  },
  header: {
    marginBottom: 16,
    alignItems: 'center',
  },
  title: {
    fontSize: 18,
    fontWeight: '600',
    color: Colors.text,
    marginBottom: 6,
    textAlign: 'center',
  },
  subtitle: {
    color: Colors.text,
    opacity: 0.8,
    textAlign: 'center',
    fontSize: 14,
  },
  formRow: {
    marginBottom: 16,
  },
  actions: {
    marginTop: 8,
    flexDirection: 'row',
    justifyContent: 'flex-end',
    gap: 12,
  },
});
